import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { Suspense, lazy, useEffect } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { measureWebVitals, observePerformance, reportBundleSize } from './utils/webVitals';

// Auth components (loaded eagerly for fast login)
import { LoginForm } from './components/auth/LoginForm';
import { StudioSignup } from './components/auth/StudioSignup';
import { StudioSignupSuccess } from './components/auth/StudioSignupSuccess';
import { StudioSignupCancel } from './components/auth/StudioSignupCancel';
import { EmailVerification } from './components/auth/EmailVerification';
import { IndividualEngineerSignup } from './components/auth/IndividualEngineerSignup';
import { EngineerSignupSuccess } from './components/auth/EngineerSignupSuccess';

// Lazy-loaded dashboard and main app components
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const ProjectsDashboard = lazy(() => import('./components/projects/ProjectsDashboard').then(m => ({ default: m.ProjectsDashboard })));
const CreateProjectForm = lazy(() => import('./components/projects/CreateProjectForm').then(m => ({ default: m.CreateProjectForm })));

// Lazy-loaded admin components (heavy and rarely used)
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const StudioAdminDashboard = lazy(() => import('./pages/StudioAdminDashboard'));
const EngineerDashboard = lazy(() => import('./pages/EngineerDashboard'));

// Lazy-loaded user and project components
const UserProfile = lazy(() => import('./pages/UserProfile'));
const PersonalSettings = lazy(() => import('./pages/PersonalSettings'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const SessionManager = lazy(() => import('./pages/SessionManager'));
const TemplateGallery = lazy(() => import('./pages/TemplateGallery'));

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FF6B35', // Orange
    },
    secondary: {
      main: '#8E24AA', // Purple
    },
    success: {
      main: '#2ED573', // Green
    },
    warning: {
      main: '#F7931E', // Yellow
    },
    error: {
      main: '#FF3742', // Red
    },
    info: {
      main: '#3D5AFE', // Blue
    },
    background: {
      default: '#0A0E1A',
      paper: 'rgba(255, 255, 255, 0.12)',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.95)',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
  },
});

// Loading component for lazy-loaded routes
const LazyLoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="50vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
  </Box>
);

function App() {
  useEffect(() => {
    // Initialize performance monitoring
    measureWebVitals();
    observePerformance();
    
    // Report bundle size in development
    if (process.env.NODE_ENV === 'development') {
      setTimeout(reportBundleSize, 2000);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SocketProvider>
          <ProjectProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<LoginForm />} />
                
                {/* Studio Signup Routes */}
                <Route path="/studio-signup" element={<StudioSignup />} />
                <Route path="/studio-signup/success" element={<StudioSignupSuccess />} />
                <Route path="/studio-signup/cancel" element={<StudioSignupCancel />} />
                
                {/* Engineer Signup Routes */}
                <Route path="/engineer-signup" element={<IndividualEngineerSignup />} />
                <Route path="/engineer-signup/success" element={<EngineerSignupSuccess />} />
                
                {/* Email Verification */}
                <Route path="/verify-email" element={<EmailVerification />} />
                
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <Dashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <ProjectsDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/projects/new" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <CreateProjectForm />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <ProjectDetails />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId/session" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <SessionManager />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/templates" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <TemplateGallery />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <UserProfile />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/*" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Studio Admin Dashboard */}
                <Route path="/studio-admin/*" element={
                  <ProtectedRoute requiredRole="STUDIO_ADMIN">
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <StudioAdminDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Engineer Dashboard */}
                <Route path="/engineer/*" element={
                  <ProtectedRoute requiredRole={['ENGINEER', 'FREELANCE_ENGINEER']}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <EngineerDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Personal Settings for Engineers */}
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <PersonalSettings />
                    </Suspense>
                  </ProtectedRoute>
                } />
                
                {/* Default Routes */}
                <Route path="/" element={<LoginForm />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
            <Toaster position="top-right" />
          </ProjectProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;